import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keyboard navigation for a searchable dropdown list.
 *
 * WHY A HOOK. The navbar has four of these pickers (partner ×2, chain, hotel) built from the same
 * markup, and none of them could be driven from the keyboard: you could type "hilton", see one result,
 * and still have to reach for the mouse. Adding arrow handling to each copy would have meant four
 * near-identical blocks that drift the moment one is touched, so the behaviour lives here once.
 *
 * HOW IT IS WIRED. The caller builds `items` in EXACTLY the order the list renders — including any
 * pseudo-option such as "All Hotels" — as [{ key, select }]. The hook owns which one is highlighted;
 * the caller spreads navProps(i) onto each row and asks isActive(i) for the highlight class.
 *
 *   const items = useMemo(() => [...], [deps]);
 *   const nav = useDropdownNav({ isOpen, items, onClose, query });
 *   <input onKeyDown={nav.onKeyDown} />
 *   <button {...nav.navProps(0)} className={`item ${nav.isActive(0) ? 'kb-active' : ''}`} />
 *
 * KEYS: ArrowDown / ArrowUp (wrapping), Home / End, Enter to select, Escape to close. Arrow keys are
 * prevented from their default of moving the caret inside the search box, which would otherwise fight
 * the list for the same keypress.
 *
 * @param {Boolean}  isOpen  - dropdown open state; the highlight resets whenever it closes
 * @param {Array}    items   - [{ key, select }] in render order
 * @param {Function} onClose - called on Escape, and after a selection
 * @param {String}   query   - current search text; the highlight resets when it changes
 */
export default function useDropdownNav({ isOpen, items = [], onClose, query = '' }) {
    // -1 = nothing highlighted. With a query typed, the first match is pre-highlighted so Enter alone
    // picks it — the common case is "type enough to leave one result, press Enter". With an empty box
    // nothing is highlighted, so Enter cannot silently pick whatever happens to be at the top of a long
    // unfiltered list.
    const [activeIndex, setActiveIndex] = useState(-1);
    const itemRefs = useRef([]);

    useEffect(() => {
        setActiveIndex(query ? 0 : -1);
    }, [query, isOpen]);

    // Keep the highlight inside the list as it shrinks under a narrowing search.
    useEffect(() => {
        if (activeIndex >= items.length) setActiveIndex(items.length > 0 ? items.length - 1 : -1);
    }, [items.length, activeIndex]);

    // block: 'nearest' so a highlight already on screen does not jerk the list around — only a move
    // past the edge scrolls.
    useEffect(() => {
        if (activeIndex < 0) return;
        const el = itemRefs.current[activeIndex];
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const onKeyDown = useCallback((e) => {
        if (!isOpen) return;
        const count = items.length;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (count) setActiveIndex(i => (i + 1) % count);
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (count) setActiveIndex(i => (i <= 0 ? count - 1 : i - 1));
                break;
            case 'Home':
                if (count) { e.preventDefault(); setActiveIndex(0); }
                break;
            case 'End':
                if (count) { e.preventDefault(); setActiveIndex(count - 1); }
                break;
            case 'Enter': {
                const item = items[activeIndex];
                if (!item) return;         // nothing highlighted — let the keypress be, don't guess
                e.preventDefault();
                item.select();
                if (onClose) onClose();
                break;
            }
            case 'Escape':
                e.preventDefault();
                if (onClose) onClose();
                break;
            default:
                break;
        }
    }, [isOpen, items, activeIndex, onClose]);

    // Hovering moves the highlight, so the mouse and the keyboard can never show two different
    // "current" rows at once.
    const navProps = useCallback((index) => ({
        ref: (el) => { itemRefs.current[index] = el; },
        onMouseEnter: () => setActiveIndex(index),
    }), []);

    const isActive = useCallback((index) => index === activeIndex, [activeIndex]);

    return { activeIndex, setActiveIndex, onKeyDown, navProps, isActive };
}
